/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { NativeSelect } from '@/components/ui/native-select';

export type WishlistSortOption = 'recently-added' | 'name-asc' | 'price-low' | 'price-high';
export type WishlistFilterOption = 'all' | 'in-stock' | 'out-of-stock' | 'on-sale';

interface WishlistSortFilterProps {
    sortValue: WishlistSortOption;
    filterValue: WishlistFilterOption;
    onSortChange: (value: WishlistSortOption) => void;
    onFilterChange: (value: WishlistFilterOption) => void;
}

/**
 * WishlistSortFilter — Sort and filter controls for the wishlist page.
 *
 * All sorting and filtering is performed client-side since the API does not support it.
 * Desktop: controls wrap in a row; selects are 240px wide.
 * Mobile: controls stack; labels are fixed 80px, selects fill remaining width.
 */
export function WishlistSortFilter({
    sortValue,
    filterValue,
    onSortChange,
    onFilterChange,
}: WishlistSortFilterProps): ReactElement {
    const { t } = useTranslation('account');
    const sortId = useId();
    const filterId = useId();

    return (
        <div className="flex w-full flex-col gap-3 md:flex-row md:flex-wrap md:content-start">
            <div className="flex w-full items-center gap-1 overflow-clip md:w-auto">
                <label
                    htmlFor={sortId}
                    className="w-20 shrink-0 text-sm leading-5 text-muted-foreground whitespace-nowrap md:w-auto">
                    {t('wishlist.sortBy')}
                </label>
                <div className="min-w-16 max-w-[448px] flex-1 md:w-[240px] md:flex-none [&_[data-slot=native-select-wrapper]]:w-full">
                    <NativeSelect
                        id={sortId}
                        className="w-full"
                        value={sortValue}
                        onChange={(e) => onSortChange(e.target.value as WishlistSortOption)}>
                        <option value="recently-added">{t('wishlist.sort.recentlyAdded')}</option>
                        <option value="name-asc">{t('wishlist.sort.nameAZ')}</option>
                        <option value="price-low">{t('wishlist.sort.priceLow')}</option>
                        <option value="price-high">{t('wishlist.sort.priceHigh')}</option>
                    </NativeSelect>
                </div>
            </div>

            <div className="flex w-full items-center gap-1 overflow-clip md:w-auto">
                <label
                    htmlFor={filterId}
                    className="w-20 shrink-0 text-sm leading-5 text-muted-foreground whitespace-nowrap md:w-auto">
                    {t('wishlist.filterBy')}
                </label>
                <div className="min-w-16 max-w-[448px] flex-1 md:w-[240px] md:flex-none [&_[data-slot=native-select-wrapper]]:w-full">
                    <NativeSelect
                        id={filterId}
                        className="w-full"
                        value={filterValue}
                        onChange={(e) => onFilterChange(e.target.value as WishlistFilterOption)}>
                        <option value="all">{t('wishlist.filter.all')}</option>
                        <option value="in-stock">{t('wishlist.filter.inStock')}</option>
                        <option value="out-of-stock">{t('wishlist.filter.outOfStock')}</option>
                        <option value="on-sale">{t('wishlist.filter.onSale')}</option>
                    </NativeSelect>
                </div>
            </div>
        </div>
    );
}

export default WishlistSortFilter;
